/* global navigator */
import React, { useEffect, useRef, useState } from 'react';
import { components as Core, useTranslation } from '../../../../core';
import { css, colors, fonts, spacing, StyleSheet } from '../../../../../styles';
import { COPY_FEEDBACK_DURATION } from '../utils/constants';

/**
 * Renders the modal title and a copyable company identifier.
 *
 * @param {object} props
 * @param {string} props.companyId
 */
export const CompanyDetailsHeader = ({ companyId }) => {
    const { t } = useTranslation();
    const [isCompanyIdCopied, setIsCompanyIdCopied] = useState(false);
    const copyFeedbackTimer = useRef(null);

    useEffect(() => () => {
        if (copyFeedbackTimer.current) clearTimeout(copyFeedbackTimer.current);
    }, []);

    const onCopyCompanyId = () => {
        if (typeof navigator === 'undefined' || !navigator.clipboard) return;

        navigator.clipboard.writeText(companyId)
            .then(() => {
                if (copyFeedbackTimer.current) clearTimeout(copyFeedbackTimer.current);
                setIsCompanyIdCopied(true);
                copyFeedbackTimer.current = setTimeout(() => {
                    setIsCompanyIdCopied(false);
                    copyFeedbackTimer.current = null;
                }, COPY_FEEDBACK_DURATION);
            })
            .catch(() => null);
    };

    const copyLabel = isCompanyIdCopied ? t('companies.details.copied') : t('companies.details.copy');

    return (
        <div className={css(styles.headerContent)}>
            <div className={css(styles.headerTitle)}>{t('companies.companyDetails')}</div>
            <div className={css(styles.companyIdContainer)}>
                <span className={css(styles.companyIdLabel)}>{t('companies.details.companyId')}</span>
                <span className={css(styles.companyIdValue)}>{companyId}</span>
                <button
                    type="button"
                    onClick={onCopyCompanyId}
                    className={css(styles.copyButton)}
                    aria-label={copyLabel}>
                    <Core.Icon
                        type={isCompanyIdCopied ? 'check' : 'copy'}
                        size={spacing.s2}
                        color={colors.primary}
                    />
                    {copyLabel}
                </button>
                <span className={css(styles.visuallyHidden)} aria-live="polite">
                    {isCompanyIdCopied ? t('companies.details.copied') : ''}
                </span>
            </div>
        </div>
    );
};

const styles = StyleSheet.create({
    headerContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: spacing.s1,
        paddingRight: spacing.s4
    },
    headerTitle: {
        ...fonts.lgNormal,
        color: colors.black
    },
    companyIdContainer: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: spacing.s1,
        minWidth: 0
    },
    companyIdLabel: {
        ...fonts.xsBold,
        color: colors.blackOpacity,
        whiteSpace: 'nowrap'
    },
    companyIdValue: {
        ...fonts.xsNormal,
        color: colors.black,
        overflowWrap: 'anywhere'
    },
    copyButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: spacing.s0,
        padding: `${spacing.s0}px ${spacing.s1}px`,
        border: `1px solid ${colors.primary}`,
        borderRadius: spacing.s3,
        backgroundColor: colors.white,
        color: colors.primary,
        cursor: 'pointer',
        ...fonts.xsNormal,
        ':hover': {
            backgroundColor: colors.primaryBackground
        },
        ':focus': {
            outline: `2px solid ${colors.primary}`,
            outlineOffset: 2
        }
    },
    visuallyHidden: {
        position: 'absolute',
        width: 1,
        height: 1,
        padding: 0,
        margin: -1,
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0
    }
});
